/**
 * The sequence checks that read what is already on file about a person.
 *
 * WHAT WAS ASKED FOR. Conor's list of signals, handed over with the UAT notes on
 * 2026-09-25, wanted a sequence to branch on what we know about somebody: "if
 * they changed job in the last three months", "if their title says founder",
 * "if they are in the US or Canada", "if they score above sixty". Until then the
 * only checks were about the sequence itself (did they accept, did they reply,
 * have we written to them), and the prospect list could filter on a signal or a
 * score while a sequence could not ask either question once somebody was in it.
 *
 * So three checks, each taking a value the author chooses. They sit on the same
 * `if` object every other check uses, as keys beside `cond`, the way the
 * message check carries its channel and scope:
 *
 *   {"cond":"has_signal","signal":"job_change","within_days":90}
 *   {"cond":"field_matches","field":"title","op":"contains","values":["founder","ceo"]}
 *   {"cond":"icp_score_gte","score":60}
 *
 * Four more joined them on 2026-09-26, from the same list. Two read what a Luma
 * import wrote about the event a person registered for, and two read when the
 * person was last seen doing something on an app:
 *
 *   {"cond":"attended_workshop"}             they were checked in at a Luma event
 *   {"cond":"no_show"}                       they registered, the event is over,
 *                                            check-in was taken there, and they
 *                                            were never checked in
 *   {"cond":"posts_active","within_days":30} their newest activity is that recent
 *   {"cond":"went_quiet","quiet_days":60}    they were active once, and nothing
 *                                            newer than that many days is on file
 *
 * NOTHING NEW IS COLLECTED FOR THEM. A signal is a row in `signals`, a field is a
 * column on the prospect, and the score is `prospects.icp_score`, all of them
 * written by the import, the enrichment and the scorer that already run. The Luma
 * checks read the `luma_status`, `luma_attended` and `luma_event_date` custom
 * fields the Luma import writes, and the activity checks read the `last_activity`
 * observations a profile scan writes. Who viewed our profile, hiring and rebrands
 * are later work with collectors of their own, and a check over them would read
 * nothing today.
 *
 * AN UNSET FACT IS FALSE, which is G5 as every other check reads it. A person with
 * no title does not match "title contains founder", a person never scored does
 * not pass a score floor of zero, and neither of those holds the sequence still.
 * They take the else arm. A signal check is different only in that "no such
 * signal" is a real answer rather than a missing one, and it is false either way.
 * The same goes for the newer four: somebody who never came in from Luma neither
 * attended nor failed to, and somebody nobody has scanned is neither active nor
 * quiet. Both take the else arm of either check.
 *
 * Everything here is pure: the words, the bounds on the document, what a stored
 * step means, and the one rule that answers a check from the facts. The worker
 * reads the facts from Postgres and asks this rule, the scheduler's double asks
 * the same rule from memory, and the browser reads the same keys back to say the
 * check out loud, so no two of them can disagree about what a check means.
 */
import { SIGNAL_TYPES, type DslCondition, type SignalType } from './types'

/** The seven checks this module answers. */
export const PROSPECT_CHECK_CONDITIONS = [
  'has_signal',
  'field_matches',
  'icp_score_gte',
  'attended_workshop',
  'no_show',
  'posts_active',
  'went_quiet',
] as const
export type ProspectCheckCondition = (typeof PROSPECT_CHECK_CONDITIONS)[number]

export function isProspectCheckCondition(cond: unknown): cond is ProspectCheckCondition {
  return typeof cond === 'string' && (PROSPECT_CHECK_CONDITIONS as readonly string[]).includes(cond)
}

/**
 * The checks that cannot be asked without a key, so never as a bare word.
 *
 * `attended_workshop` and `no_show` ask a fixed question and can be written as
 * `"if": "no_show"`. The other five name what they look for, and a bare word
 * would be a check nothing can answer.
 */
export const PROSPECT_CHECKS_NEEDING_KEYS = [
  'has_signal',
  'field_matches',
  'icp_score_gte',
  'posts_active',
  'went_quiet',
] as const satisfies readonly ProspectCheckCondition[]
export type KeyedProspectCheckCondition = (typeof PROSPECT_CHECKS_NEEDING_KEYS)[number]

export function prospectCheckNeedsKeys(cond: unknown): cond is KeyedProspectCheckCondition {
  return typeof cond === 'string' && (PROSPECT_CHECKS_NEEDING_KEYS as readonly string[]).includes(cond)
}

/**
 * The fields a field check can read.
 *
 * The three Conor named. Each is a plain column on the prospect row, filled by
 * the import or the enrichment, so a check over them reads the row and nothing
 * else. `country` is the ISO 3166-1 alpha-2 code the import resolves from the
 * free-text location, never the location itself.
 */
export const FIELD_CHECK_FIELDS = ['title', 'industry', 'country', 'research_notes'] as const
export type NamedFieldCheckField = (typeof FIELD_CHECK_FIELDS)[number]

/**
 * Any field a file carried or enrichment found, by its variable key.
 *
 *   > once a gojibasel researches a page and once we enrich a contact and
 *   > discover information on them, can we come up with some if statements
 *   > around that data
 *
 * So a check can read `custom_field:<key>`, where the key is the one the
 * Variables screen shows: a column from a file (`custom_field:credits`) or a
 * value off the profile card (`custom_field:profile_followers`). And
 * `research_notes` reads what the research of their posts concluded: the
 * reading's summary and the topics it named.
 */
export const CUSTOM_FIELD_CHECK_PREFIX = 'custom_field:'
export type CustomFieldCheckField = `custom_field:${string}`
export type FieldCheckField = NamedFieldCheckField | CustomFieldCheckField

const CUSTOM_FIELD_KEY = /^[a-z][a-z0-9_]*$/

/** The variable key a `custom_field:<key>` field reads, or null for any other field. */
export function customFieldKeyOf(field: unknown): string | null {
  if (typeof field !== 'string' || !field.startsWith(CUSTOM_FIELD_CHECK_PREFIX)) return null
  const key = field.slice(CUSTOM_FIELD_CHECK_PREFIX.length)
  return CUSTOM_FIELD_KEY.test(key) ? key : null
}

/**
 * How a field is compared.
 *
 * `contains` holds when one of the values appears in the field at the start of a
 * word, ignoring case. At the start of a word and not anywhere, because "CTO"
 * appears inside "Director" and a check for chief technology officers that
 * passed every director would be worse than no check. "Founder" still matches
 * "Co-Founder" and "engineer" still matches "Engineering Manager".
 *
 * `in` holds when the field IS one of the values, ignoring case and spacing.
 */
export const FIELD_CHECK_OPS = ['contains', 'in', 'equals', 'exists'] as const
export type FieldCheckOp = (typeof FIELD_CHECK_OPS)[number]

/**
 * How a file's column or an enriched value is compared. `equals` holds when the
 * value is one of the values given, ignoring case and spacing. `contains` is the
 * word-start match every field uses. `exists` holds when the person has any
 * value at all, and takes no values.
 */
export const CUSTOM_FIELD_CHECK_OPS = ['equals', 'contains', 'exists'] as const satisfies readonly FieldCheckOp[]

/**
 * Which comparisons each field takes, the first being the one a picker starts on.
 *
 * Title contains, industry in and country in are the three that were asked for.
 * Industry takes `contains` as well, because the industry on file is free text
 * and not a list: a read-only count on live on 2026-09-25 found 194 different
 * industries across 689 people, many of them sentences like "B2B events, trade
 * shows & media", so an exact match would be a check almost nobody can write.
 * Country takes `in` only. It is a two letter code, and "contains U" is not a
 * question anybody means.
 */
export const FIELD_CHECK_OPS_BY_FIELD: Readonly<Record<NamedFieldCheckField, readonly FieldCheckOp[]>> = {
  title: ['contains'],
  industry: ['in', 'contains'],
  country: ['in'],
  research_notes: ['contains', 'exists'],
}

/** The comparisons a field takes, or null when it is not a field a check can read. */
export function fieldCheckOps(field: unknown): readonly FieldCheckOp[] | null {
  if (typeof field === 'string' && (FIELD_CHECK_FIELDS as readonly string[]).includes(field)) {
    return FIELD_CHECK_OPS_BY_FIELD[field as NamedFieldCheckField]
  }
  return customFieldKeyOf(field) === null ? null : CUSTOM_FIELD_CHECK_OPS
}

/**
 * Bounds on the DOCUMENT, not product limits, in the posture `max_messages` and
 * `park.days` take in the step schema. A lookback of zero days or of ten years,
 * five hundred words to look for, or a score above the scale's top are not checks
 * anybody wrote on purpose, so they are refused as a shape rather than carried to
 * the scheduler to be quietly read some other way.
 *
 * The score's range is the scale the scorer writes, 0 to 100, which is also what
 * the prospect list's score floor accepts.
 */
export const SIGNAL_WITHIN_DAYS_MIN = 1
export const SIGNAL_WITHIN_DAYS_MAX = 365
export const FIELD_CHECK_MAX_VALUES = 50
export const FIELD_CHECK_MAX_VALUE_LENGTH = 200
export const ICP_SCORE_MIN = 0
export const ICP_SCORE_MAX = 100
/**
 * How many days an activity check counts, on the same document bounds as a
 * signal lookback and for the same reason: nobody means "active in the last
 * zero days" or "quiet for ten years".
 */
export const ACTIVITY_DAYS_MIN = SIGNAL_WITHIN_DAYS_MIN
export const ACTIVITY_DAYS_MAX = SIGNAL_WITHIN_DAYS_MAX

/** The keys these checks add to the `if` object, as the document spells them. */
export const PROSPECT_CHECK_KEYS = ['signal', 'within_days', 'field', 'op', 'values', 'score', 'quiet_days'] as const
export type ProspectCheckKey = (typeof PROSPECT_CHECK_KEYS)[number]

/**
 * Which keys each check reads. A key on any other check is refused.
 *
 * `within_days` is read by two checks and means the same on both, how many days
 * back something counts: a signal seen that recently, or activity seen that
 * recently. On a signal check it may be left out, on an activity check it is
 * the question.
 */
export const PROSPECT_CHECK_KEYS_BY_CONDITION: Readonly<Record<ProspectCheckCondition, readonly ProspectCheckKey[]>> = {
  has_signal: ['signal', 'within_days'],
  field_matches: ['field', 'op', 'values'],
  icp_score_gte: ['score'],
  attended_workshop: [],
  no_show: [],
  posts_active: ['within_days'],
  went_quiet: ['quiet_days'],
}

/** The keys as they arrive on a stored `if`, before anything has checked them. */
export type ProspectCheckKeys = Partial<Record<ProspectCheckKey, unknown>>

/**
 * The same keys on a read-back, typed loosely, for the API's step views and the
 * screens that read them. Absent on every other check.
 */
export interface ProspectCheckWire {
  signal?: string
  within_days?: number
  field?: string
  op?: string
  values?: string[]
  score?: number
  quiet_days?: number
}

/**
 * A check, resolved: what it asks, with its values checked and tidied.
 *
 * `luma` asks about the event a person registered for on Luma, `attended` being
 * "they were checked in" and `no_show` "they registered and were not". `activity`
 * asks when they were last seen doing something, `active` being "within `days`"
 * and `quiet` "not within `days`, though seen at some point".
 */
export type ProspectCheck =
  | { kind: 'signal'; signal: SignalType; withinDays: number | null }
  | { kind: 'field'; field: FieldCheckField; op: FieldCheckOp; values: string[] }
  | { kind: 'score'; atLeast: number }
  | { kind: 'luma'; asks: 'attended' | 'no_show' }
  | { kind: 'activity'; asks: 'active' | 'quiet'; days: number }

/** One problem with a check's keys, on the key it names. */
export interface ProspectCheckProblem {
  key: ProspectCheckKey
  message: string
}

const OWNER_WORDS: Record<ProspectCheckCondition, string> = {
  has_signal: 'the signal check (has_signal)',
  field_matches: 'the field check (field_matches)',
  icp_score_gte: 'the score check (icp_score_gte)',
  attended_workshop: 'the attended check (attended_workshop)',
  no_show: 'the no-show check (no_show)',
  posts_active: 'the active check (posts_active)',
  went_quiet: 'the quiet check (went_quiet)',
}

/** Every check that reads a key, in the table's order, so a refusal can name them all. */
function ownersOf(key: ProspectCheckKey): ProspectCheckCondition[] {
  return PROSPECT_CHECK_CONDITIONS.filter((cond) => PROSPECT_CHECK_KEYS_BY_CONDITION[cond].includes(key))
}

function isSignalType(value: unknown): value is SignalType {
  return typeof value === 'string' && (SIGNAL_TYPES as readonly string[]).includes(value)
}

export function isFieldCheckField(value: unknown): value is FieldCheckField {
  return fieldCheckOps(value) !== null
}

function isFieldCheckOp(value: unknown): value is FieldCheckOp {
  return typeof value === 'string' && (FIELD_CHECK_OPS as readonly string[]).includes(value)
}

function listWords(words: readonly string[]): string {
  if (words.length <= 1) return words.join('')
  return `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}`
}

/** Spacing tidied, so " US " and "US" are one value. Case is kept for display. */
function tidy(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

const COUNTRY_CODE = /^[A-Za-z]{2}$/

/**
 * Everything wrong with a check's keys, in sentences, each on the key it names.
 *
 * Asked by the step schema at save, so a document that reaches the scheduler
 * has already passed it. Returns nothing for a check this module does not own
 * as long as it carries none of these keys, which is what lets the schema ask
 * it of every `if` without knowing which check it is looking at.
 */
export function prospectCheckProblems(
  cond: DslCondition | string | null | undefined,
  keys: ProspectCheckKeys,
): ProspectCheckProblem[] {
  const problems: ProspectCheckProblem[] = []
  const owned: readonly ProspectCheckKey[] = isProspectCheckCondition(cond) ? PROSPECT_CHECK_KEYS_BY_CONDITION[cond] : []
  for (const key of PROSPECT_CHECK_KEYS) {
    if (keys[key] === undefined || owned.includes(key)) continue
    problems.push({ key, message: `only ${listWords(ownersOf(key).map((owner) => OWNER_WORDS[owner]))} takes ${key}` })
  }
  if (cond === 'has_signal') problems.push(...signalProblems(keys))
  if (cond === 'field_matches') problems.push(...fieldProblems(keys))
  if (cond === 'icp_score_gte') problems.push(...scoreProblems(keys))
  if (cond === 'posts_active') problems.push(...activityDaysProblems('within_days', keys.within_days))
  if (cond === 'went_quiet') problems.push(...activityDaysProblems('quiet_days', keys.quiet_days))
  return problems
}

/**
 * The one number an activity check needs. Required, with no default, because a
 * default here would be a number nobody chose deciding who gets a message.
 */
function activityDaysProblems(key: 'within_days' | 'quiet_days', days: unknown): ProspectCheckProblem[] {
  if (typeof days === 'number' && Number.isInteger(days) && days >= ACTIVITY_DAYS_MIN && days <= ACTIVITY_DAYS_MAX) return []
  const meaning =
    key === 'within_days'
      ? 'how many days back their newest activity may be for them to count as active'
      : 'how many days without any activity on file counts as gone quiet'
  const example = key === 'within_days' ? '{"cond":"posts_active","within_days":30}' : '{"cond":"went_quiet","quiet_days":60}'
  return [
    {
      key,
      message: `${key} is ${meaning}, a whole number from ${ACTIVITY_DAYS_MIN} to ${ACTIVITY_DAYS_MAX}, as in ${example}`,
    },
  ]
}

function signalProblems(keys: ProspectCheckKeys): ProspectCheckProblem[] {
  const problems: ProspectCheckProblem[] = []
  if (keys.signal === undefined) {
    problems.push({
      key: 'signal',
      message: `a signal check needs the signal it looks for, one of ${listWords(SIGNAL_TYPES)}, as in {"cond":"has_signal","signal":"job_change"}`,
    })
  } else if (!isSignalType(keys.signal)) {
    problems.push({
      key: 'signal',
      message: `${JSON.stringify(keys.signal)} is not a signal this platform records. Use ${listWords(SIGNAL_TYPES)}`,
    })
  }
  if (keys.within_days !== undefined) {
    const days = keys.within_days
    if (typeof days !== 'number' || !Number.isInteger(days) || days < SIGNAL_WITHIN_DAYS_MIN || days > SIGNAL_WITHIN_DAYS_MAX) {
      problems.push({
        key: 'within_days',
        message: `within_days is how many days back the signal may have been seen, a whole number from ${SIGNAL_WITHIN_DAYS_MIN} to ${SIGNAL_WITHIN_DAYS_MAX}. Leave it out to count a signal seen at any time`,
      })
    }
  }
  return problems
}

function fieldProblems(keys: ProspectCheckKeys): ProspectCheckProblem[] {
  const problems: ProspectCheckProblem[] = []
  const field = keys.field
  if (field === undefined) {
    problems.push({
      key: 'field',
      message: `a field check needs the field it reads, one of ${listWords(FIELD_CHECK_FIELDS)}, or custom_field:<key> for a column from a file or a value found on their profile`,
    })
  } else if (!isFieldCheckField(field)) {
    problems.push({
      key: 'field',
      message: `${JSON.stringify(field)} is not a field this check can read. Use ${listWords(FIELD_CHECK_FIELDS)}, or custom_field:<key> with a key in lowercase letters, digits and underscores, as in custom_field:credits`,
    })
  }
  const ops = fieldCheckOps(field) ?? FIELD_CHECK_OPS
  if (keys.op === undefined) {
    problems.push({
      key: 'op',
      message: isFieldCheckField(field)
        ? `say how the ${field} is compared with op, which for ${field} is ${listWords(ops)}`
        : `say how the field is compared with op, one of ${listWords(FIELD_CHECK_OPS)}`,
    })
  } else if (!isFieldCheckOp(keys.op) || !ops.includes(keys.op)) {
    problems.push({
      key: 'op',
      message: isFieldCheckField(field)
        ? `${field} cannot be compared with ${JSON.stringify(keys.op)}. Use ${listWords(ops)}`
        : `${JSON.stringify(keys.op)} is not a comparison. Use ${listWords(FIELD_CHECK_OPS)}`,
    })
  }
  const values = keys.values
  // `exists` asks whether there is a value at all, so it takes none to compare.
  if (keys.op === 'exists') {
    if (values !== undefined && !(Array.isArray(values) && values.length === 0)) {
      problems.push({ key: 'values', message: 'exists asks whether the person has any value at all, so leave values out' })
    }
    return problems
  }
  if (values === undefined) {
    problems.push({ key: 'values', message: 'a field check needs values, a list of at least one word or phrase to look for' })
    return problems
  }
  if (!Array.isArray(values) || values.length === 0) {
    problems.push({ key: 'values', message: 'values is a list of at least one word or phrase, as in ["founder","ceo"]' })
    return problems
  }
  if (values.length > FIELD_CHECK_MAX_VALUES) {
    problems.push({ key: 'values', message: `a field check looks for at most ${FIELD_CHECK_MAX_VALUES} values` })
  }
  for (const value of values) {
    if (typeof value !== 'string' || tidy(value) === '') {
      problems.push({ key: 'values', message: 'every value has to be some words, and a blank one matches nothing' })
      break
    }
    if (value.length > FIELD_CHECK_MAX_VALUE_LENGTH) {
      problems.push({ key: 'values', message: `a value is at most ${FIELD_CHECK_MAX_VALUE_LENGTH} characters` })
      break
    }
    if (field === 'country' && !COUNTRY_CODE.test(tidy(value))) {
      problems.push({
        key: 'values',
        message: `${JSON.stringify(value)} is not a country code. A country is stored as its two letter code, such as US, GB or ID`,
      })
      break
    }
  }
  return problems
}

function scoreProblems(keys: ProspectCheckKeys): ProspectCheckProblem[] {
  const score = keys.score
  if (typeof score === 'number' && Number.isFinite(score) && score >= ICP_SCORE_MIN && score <= ICP_SCORE_MAX) return []
  return [
    {
      key: 'score',
      message: `a score check needs score, the lowest fit score that passes, a number from ${ICP_SCORE_MIN} to ${ICP_SCORE_MAX}`,
    },
  ]
}

/**
 * What a stored check asks, or null when it is not one of these or cannot be read.
 *
 * Null for a check whose keys are wrong, rather than a guess at what was meant,
 * because the save refused those keys with a sentence already. A reader that
 * meets one anyway (a row written before a rule tightened) gets G5: the check
 * cannot be answered, so it is false and takes the else arm.
 *
 * Values are tidied and de-duplicated ignoring case, in the order written, and a
 * country is upper-cased, so the scheduler, the differ and the screen all read
 * one spelling.
 */
export function resolveProspectCheck(
  cond: DslCondition | string | null | undefined,
  keys: ProspectCheckKeys = {},
): ProspectCheck | null {
  if (!isProspectCheckCondition(cond)) return null
  if (prospectCheckProblems(cond, keys).length > 0) return null
  if (cond === 'has_signal') {
    return {
      kind: 'signal',
      signal: keys.signal as SignalType,
      withinDays: typeof keys.within_days === 'number' ? keys.within_days : null,
    }
  }
  if (cond === 'icp_score_gte') return { kind: 'score', atLeast: keys.score as number }
  if (cond === 'attended_workshop') return { kind: 'luma', asks: 'attended' }
  if (cond === 'no_show') return { kind: 'luma', asks: 'no_show' }
  if (cond === 'posts_active') return { kind: 'activity', asks: 'active', days: keys.within_days as number }
  if (cond === 'went_quiet') return { kind: 'activity', asks: 'quiet', days: keys.quiet_days as number }
  const field = keys.field as FieldCheckField
  const seen = new Set<string>()
  const values: string[] = []
  for (const raw of (keys.values ?? []) as string[]) {
    const value = field === 'country' ? tidy(raw).toUpperCase() : tidy(raw)
    const key = value.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    values.push(value)
  }
  return { kind: 'field', field, op: keys.op as FieldCheckOp, values }
}

/** The condition word a resolved check is written with. */
export function prospectCheckCondition(check: ProspectCheck): ProspectCheckCondition {
  if (check.kind === 'signal') return 'has_signal'
  if (check.kind === 'field') return 'field_matches'
  if (check.kind === 'luma') return check.asks === 'attended' ? 'attended_workshop' : 'no_show'
  if (check.kind === 'activity') return check.asks === 'active' ? 'posts_active' : 'went_quiet'
  return 'icp_score_gte'
}

/**
 * A resolved check as the keys a document or a read-back carries.
 *
 * `within_days` is left out when the check counts a signal from any time, which
 * is how the author wrote it, so reading a step back and writing it again does
 * not grow a key.
 */
export function prospectCheckKeys(check: ProspectCheck): ProspectCheckKeys {
  if (check.kind === 'signal') {
    return { signal: check.signal, ...(check.withinDays === null ? {} : { within_days: check.withinDays }) }
  }
  if (check.kind === 'field') {
    return check.op === 'exists' ? { field: check.field, op: check.op } : { field: check.field, op: check.op, values: [...check.values] }
  }
  if (check.kind === 'luma') return {}
  if (check.kind === 'activity') return check.asks === 'active' ? { within_days: check.days } : { quiet_days: check.days }
  return { score: check.atLeast }
}

/**
 * The check as one comparable line, for the two differs.
 *
 * Changing which signal, how recent, which field, how it is compared, what it
 * looks for or the score floor changes who gets the step, so each is a change of
 * shape the way changing the check itself is. Values compare as a set, ignoring
 * case, since "founder, ceo" and "CEO, founder" pass exactly the same people.
 */
export function prospectCheckWords(check: ProspectCheck): string {
  const cond = prospectCheckCondition(check)
  if (check.kind === 'signal') return `${cond}:${check.signal}:${check.withinDays ?? 'any'}`
  if (check.kind === 'score') return `${cond}:${check.atLeast}`
  if (check.kind === 'luma') return cond
  if (check.kind === 'activity') return `${cond}:${check.days}`
  const values = [...new Set(check.values.map((value) => value.toLowerCase()))].sort()
  return `${cond}:${check.field}:${check.op}:${JSON.stringify(values)}`
}

/**
 * What is on file about one person, as much as a check needs.
 *
 * Null fields are unknown, never empty strings. `signals` needs to hold only the
 * newest sighting of each type asked about, since a lookback only ever asks
 * whether the newest one is recent enough, and the worker reads exactly that.
 */
export interface ProspectFacts {
  title: string | null
  industry: string | null
  country: string | null
  icpScore: number | null
  signals: readonly { type: string; detectedAt: Date }[]
  /**
   * What a Luma import wrote about the event this person registered for, or null
   * when they never came in from Luma. Optional so a reader built before the
   * Luma checks still type checks, and absent reads as null.
   */
  luma?: LumaFacts | null
  /**
   * The newest moment they were seen doing something, across the apps a scan
   * read, or null when no scan dated anything. Optional for the reason above.
   */
  lastActivityAt?: Date | null
  /**
   * Every discovered field's value, the file's columns and what the newest
   * profile card found (`discoveredFieldValues`), for a `custom_field:<key>`
   * check. Absent when the reader did not read them.
   */
  customFields?: Readonly<Record<string, string>>
  /**
   * What the research of their posts concluded, the reading's summary and the
   * topics it named, or null when nobody has read their posts. Absent when the
   * reader did not read it.
   */
  researchNotes?: string | null
}

/**
 * One field's value off the facts. Undefined when the reader did not read it,
 * null when it was read and is not there.
 */
function fieldValueOf(facts: ProspectFacts, field: FieldCheckField): string | null | undefined {
  const key = customFieldKeyOf(field)
  if (key !== null) {
    if (facts.customFields === undefined) return undefined
    return facts.customFields[key] ?? null
  }
  if (field === 'research_notes') return facts.researchNotes
  if (field === 'title' || field === 'industry' || field === 'country') return facts[field]
  return undefined
}

/**
 * The Luma custom fields a check reads, as the import writes them.
 *
 * `status` is Luma's approval status (`approved` for somebody registered and
 * let in). `attended` is whether `luma_attended` says yes, which the import
 * writes only for a guest Luma has a check-in time for. `eventDate` is the
 * event's start date, `YYYY-MM-DD`, or null when the import left the event out.
 *
 * `checkInTaken` is whether anybody else on file from the same event was checked
 * in, which is how a no-show is told apart from an event where nobody scanned
 * a ticket. Null when the reader did not ask, which it only does for `no_show`.
 */
export interface LumaFacts {
  status: string | null
  attended: boolean
  eventDate: string | null
  checkInTaken: boolean | null
}

/** Luma's approval status for somebody registered and let in. */
export const LUMA_REGISTERED_STATUS = 'approved'

const EVENT_DATE = /^\d{4}-\d{2}-\d{2}$/

const DAY_MS = 24 * 60 * 60 * 1000

/** Lower case, spacing tidied and compatibility forms folded, for comparing words. */
function fold(value: string): string {
  return tidy(value.normalize('NFKC')).toLowerCase()
}

const WORD_CHARACTER = /[\p{L}\p{N}]/u

/** Whether `needle` appears in `haystack` at the start of a word. Both already folded. */
export function containsAtWordStart(haystack: string, needle: string): boolean {
  if (needle === '') return false
  let at = haystack.indexOf(needle)
  while (at >= 0) {
    if (at === 0 || !WORD_CHARACTER.test(haystack.charAt(at - 1))) return true
    at = haystack.indexOf(needle, at + 1)
  }
  return false
}

/**
 * THE RULE. Whether a check holds for a person, or null when it cannot be told.
 *
 * Null for a person there are no facts for (not in this brand, or erased), for
 * a field nothing has filled, and for a score nobody computed. The scheduler reads
 * null exactly as it reads false, G5, and records the difference, because "they
 * are not a founder" and "we do not know their title" are two different things to
 * fix. A missing signal is false rather than null: no row is the answer.
 * Null too for a Luma check on somebody who never came in from Luma, and for an
 * activity check on somebody no scan has dated, see `answerLumaCheck`.
 *
 * `at` is the moment the check is asked, which is where a lookback counts back
 * from. A signal dated after it still counts, since it was seen. An activity
 * exactly `days` old is still recent, so "active within 30 days" and "quiet for
 * 30 days" never both hold for one person at one moment.
 */
export function answerProspectCheck(check: ProspectCheck, facts: ProspectFacts | null, at: Date): boolean | null {
  if (facts === null) return null
  if (check.kind === 'signal') {
    const floor = check.withinDays === null ? null : at.getTime() - check.withinDays * DAY_MS
    return facts.signals.some(
      (signal) => signal.type === check.signal && (floor === null || signal.detectedAt.getTime() >= floor),
    )
  }
  if (check.kind === 'score') {
    const score = facts.icpScore
    if (score === null || !Number.isFinite(score)) return null
    return score >= check.atLeast
  }
  if (check.kind === 'luma') return answerLumaCheck(check.asks, facts.luma ?? null, at)
  if (check.kind === 'activity') {
    const seen = facts.lastActivityAt ?? null
    if (seen === null || Number.isNaN(seen.getTime())) return null
    const recent = seen.getTime() >= at.getTime() - check.days * DAY_MS
    return check.asks === 'active' ? recent : !recent
  }
  const raw = fieldValueOf(facts, check.field)
  // `exists` is answered by the absence itself, once the fields were read. A
  // reader that did not read them (undefined) cannot say, so that stays null.
  if (check.op === 'exists') return raw === undefined ? null : raw !== null && tidy(raw) !== ''
  if (raw === undefined || raw === null || tidy(raw) === '') return null
  const value = fold(raw)
  if (check.op === 'in' || check.op === 'equals') return check.values.some((wanted) => fold(wanted) === value)
  return check.values.some((wanted) => containsAtWordStart(value, fold(wanted)))
}

/**
 * The two Luma checks, from what the import wrote.
 *
 * Somebody who never came in from Luma is null for both: they neither attended
 * nor failed to. Somebody checked in attended and is not a no-show.
 *
 * A NO-SHOW NEEDS FOUR THINGS, and each missing one reads false or unknown
 * rather than a guess. They registered and were let in (`approved`, not merely
 * invited or waitlisted). The event is over, meaning its date is before the day
 * the check is asked, since somebody registered for tonight has not missed it
 * yet. Check-in was taken at that event, meaning somebody else from it is on
 * file as checked in. And they were not. The third is the one that matters most:
 * plenty of events never scan a ticket, and without it every guest of such an
 * event would read as a no-show and be written to as one.
 */
function answerLumaCheck(asks: 'attended' | 'no_show', luma: LumaFacts | null, at: Date): boolean | null {
  if (luma === null) return null
  if (asks === 'attended') return luma.attended
  if (luma.attended) return false
  if (luma.status === null || luma.status.trim().toLowerCase() !== LUMA_REGISTERED_STATUS) return false
  if (luma.eventDate === null || !EVENT_DATE.test(luma.eventDate)) return null
  if (luma.eventDate >= at.toISOString().slice(0, 10)) return false
  if (luma.checkInTaken !== true) return null
  return true
}

// ---------------------------------------------------------------------------
// The same two questions as filters on the prospect list
// ---------------------------------------------------------------------------

/**
 * "Only people in this industry" and "only people in these countries", as the
 * prospect list and `GET /prospect-search` ask them.
 *
 * They read the way a field check reads, so a person the list shows under a
 * filter is a person the check passes: an industry has a word starting with what
 * was typed, ignoring case (the field check's `contains`), and a country is one
 * of the two letter codes given (the field check's `in`). An industry and a
 * country nothing has filled in match neither.
 */

/** Whether a person's industry passes the industry filter. */
export function industryFilterMatches(industry: string | null | undefined, wanted: string): boolean {
  if (industry === null || industry === undefined || tidy(industry) === '') return false
  return containsAtWordStart(fold(industry), fold(wanted))
}

const REGEX_SPECIAL = /[\\^$.|?*+()[\]{}]/g

/**
 * The industry filter as a Postgres regular expression, for `~*` against the
 * industry with its spacing tidied: the typed words, spacing tidied and every
 * special character escaped, at the start of the value or after a character
 * that is not a letter or a digit. Case is left to `~*`.
 */
export function industryFilterPattern(wanted: string): string {
  return `(^|[^[:alnum:]])${tidy(wanted).replace(REGEX_SPECIAL, (character) => `\\${character}`)}`
}

/**
 * A country filter as typed or linked, "US, ca" or "US CA", read as codes.
 *
 * Upper-cased and de-duplicated in the order given. Anything that is not a two
 * letter code is refused by name rather than dropped, because a filter that
 * quietly ignored "Canada" would show a list that looks filtered and is not.
 */
export function parseCountryFilter(raw: string): { codes: string[] } | { error: string } {
  const parts = raw.split(/[\s,]+/).filter((part) => part !== '')
  if (parts.length === 0) return { codes: [] }
  if (parts.length > FIELD_CHECK_MAX_VALUES) return { error: `at most ${FIELD_CHECK_MAX_VALUES} countries` }
  const codes: string[] = []
  for (const part of parts) {
    if (!COUNTRY_CODE.test(part)) {
      return { error: `${JSON.stringify(part)} is not a country code. Use two letter codes such as US, GB or ID` }
    }
    const code = part.toUpperCase()
    if (!codes.includes(code)) codes.push(code)
  }
  return { codes }
}

/**
 * What the research of somebody's posts concluded, as one text a check reads:
 * the reading's summary, then the topics it named. Null when there is no
 * reading, or it says nothing.
 */
export function researchNotesOf(summary: unknown, topics: unknown): string | null {
  const parts: string[] = []
  if (typeof summary === 'string' && tidy(summary) !== '') parts.push(tidy(summary))
  if (Array.isArray(topics)) {
    for (const item of topics) {
      const topic = item !== null && typeof item === 'object' ? (item as { topic?: unknown }).topic : item
      if (typeof topic === 'string' && tidy(topic) !== '') parts.push(tidy(topic))
    }
  }
  return parts.length === 0 ? null : parts.map((part) => part.replace(/[.\s]+$/, '')).join('. ')
}
